import React, { useState, useEffect } from 'react';
import {
  Box, Switch, Select, MenuItem, TextField,
  Autocomplete, Chip, Button, Tooltip, InputAdornment, Skeleton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  EmailOutlined, AccessTime, AccessTimeFilled, EditOutlined, CalendarMonthOutlined, KeyboardArrowDown
} from '@mui/icons-material';

import { useSelector, useDispatch } from 'react-redux';
import { fetchAIPlanDropdown } from '../../../store/aiDevSlice';
import { fetchScrumRole, clearScrumData } from '../../../store/scrumSlice';
import { REGEX } from '../../../shared/regex';
import { RECURRENCE_FREQUENCIES, REMINDER_TIME_OPTIONS, DEFAULT_REMINDER_TIME, BACKEND_SCRUM_ROLES, USER_ROLES, PROJECT_TYPES } from '../../../shared/appConstants';
import TimePickerModal, { format12h } from '../../../shared/TimePickerModal';
import OverflowTooltip from '../../../shared/OverflowTooltip';
import { InAppIcon, UserIcon, WarningBellIcon } from './SettingsIcons';
import './SettingsTab.scss';
import { api, ENDPOINTS } from '../../../services';
import { toast } from 'react-toastify';

const WEEK_DAYS = [
  { id: 'M', label: 'M' },
  { id: 'T', label: 'T' },
  { id: 'W', label: 'W' },
  { id: 'Th', label: 'T' },
  { id: 'F', label: 'F' },
  { id: 'S', label: 'S' },
  { id: 'Su', label: 'S' }
];

const MWF = ['M', 'W', 'F'];
const TTS = ['T', 'Th', 'S'];

const mapDayIdToNum = (dayId) => {
  switch (dayId) {
    case 'Su': return 0;
    case 'M': return 1;
    case 'T': return 2;
    case 'W': return 3;
    case 'Th': return 4;
    case 'F': return 5;
    case 'S': return 6;
    default: return null;
  }
};

const mapNumToDayId = (num) => {
  switch (Number(num)) {
    case 0: return 'Su';
    case 1: return 'M';
    case 2: return 'T';
    case 3: return 'W';
    case 4: return 'Th';
    case 5: return 'F';
    case 6: return 'S';
    default: return null;
  }
};

const mapUiTimeToBackendTime = (uiTime) => {
  switch (uiTime) {
    case '15min': return '15';
    case '30min': return '30';
    case '45min': return '45';
    case '1h': return '60';
    case '2h': return '120';
    default: return '60';
  }
};

const mapBackendTimeToUiTime = (backendTime) => {
  const numTime = Number(backendTime);
  switch (numTime) {
    case 15: return '15min';
    case 30: return '30min';
    case 45: return '45min';
    case 60: return '1h';
    case 120: return '2h';
    default: return '1h';
  }
};

const getBrowserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';

const getInitialUserConfig = () => ({
  enabled: true,
  frequency: RECURRENCE_FREQUENCIES.DAILY,
  time: '',
  to: [],
  cc: [],
  selected_days: [],
  until_date: '',
  time_zone: getBrowserTimeZone()
});

const getInitialProjectConfig = () => ({
  dsm_updates_on_mail: {
    enabled: false,
    send_time: '',
    recurrence: RECURRENCE_FREQUENCIES.DAILY,
    selected_days: [],
    until_date: '',
    time_zone: getBrowserTimeZone()
  },
  task_stagnation_reminder: {
    enabled: false,
    reminder_time: '1h',
    channels: {
      email: true,
      in_app: false
    },
    time_zone: getBrowserTimeZone()
  },
  report_email_configuration: {
    cc: [],
    to: []
  },
  standup_not_updated_reminder: {
    enabled: false,
    reminder_time: '1h',
    channels: {
      email: true,
      in_app: true
    },
    time_zone: getBrowserTimeZone()
  }
});

const mapSettingToUiProjectConfig = (json_value) => {
  const dsm = json_value?.dsm_updates_on_mail || {};
  const taskStagnation = json_value?.task_stagnation_reminder || {};
  const standupNotUpdated = json_value?.standup_not_updated_reminder || {};
  const reportEmail = json_value?.report_email_configuration || {};

  return {
    dsm_updates_on_mail: {
      enabled: !!dsm.enabled,
      send_time: dsm.send_time || '',
      recurrence: dsm.recurrence === 'ALTERNATE' ? RECURRENCE_FREQUENCIES.ALTERNATE_DAY : (dsm.recurrence || RECURRENCE_FREQUENCIES.DAILY),
      selected_days: dsm.recurrence_day ? dsm.recurrence_day.map(mapNumToDayId).filter(Boolean) : [],
      until_date: dsm.until || '',
      time_zone: dsm.time_zone || getBrowserTimeZone()
    },
    task_stagnation_reminder: {
      enabled: !!taskStagnation.enabled,
      reminder_time: mapBackendTimeToUiTime(taskStagnation.time),
      channels: {
        email: !!taskStagnation.channels?.email,
        in_app: !!taskStagnation.channels?.in_app
      },
      time_zone: taskStagnation.time_zone || getBrowserTimeZone()
    },
    report_email_configuration: {
      cc: reportEmail.cc || [],
      to: reportEmail.to || []
    },
    standup_not_updated_reminder: {
      enabled: !!standupNotUpdated.enabled,
      reminder_time: mapBackendTimeToUiTime(standupNotUpdated.time),
      channels: {
        email: !!standupNotUpdated.channels?.email,
        in_app: !!standupNotUpdated.channels?.in_app
      },
      time_zone: standupNotUpdated.time_zone || getBrowserTimeZone()
    }
  };
};

const mapUiProjectConfigToSetting = (config, timeZone = getBrowserTimeZone()) => {
  const dsm = config.dsm_updates_on_mail || {};
  const taskStagnation = config.task_stagnation_reminder || {};
  const standupNotUpdated = config.standup_not_updated_reminder || {};
  const reportEmail = config.report_email_configuration || {};

  const dsmRecDays = (dsm.selected_days || []).map(mapDayIdToNum).filter(n => n !== null);

  return {
    dsm_updates_on_mail: {
      enabled: !!dsm.enabled,
      send_time: dsm.send_time || '',
      recurrence: dsm.recurrence === RECURRENCE_FREQUENCIES.ALTERNATE_DAY ? 'ALTERNATE' : (dsm.recurrence || 'DAILY'),
      recurrence_day: dsmRecDays,
      until: dsm.until_date || null,
      time_zone: timeZone
    },
    task_stagnation_reminder: {
      enabled: !!taskStagnation.enabled,
      time: mapUiTimeToBackendTime(taskStagnation.reminder_time),
      channels: {
        email: !!taskStagnation.channels?.email,
        in_app: !!taskStagnation.channels?.in_app
      },
      time_zone: timeZone
    },
    report_email_configuration: {
      cc: reportEmail.cc || [],
      to: reportEmail.to || []
    },
    standup_not_updated_reminder: {
      enabled: !!standupNotUpdated.enabled,
      time: mapUiTimeToBackendTime(standupNotUpdated.reminder_time),
      channels: {
        email: !!standupNotUpdated.channels?.email,
        in_app: !!standupNotUpdated.channels?.in_app
      },
      time_zone: timeZone
    }
  };
};

const DaySelector = ({
  mode, // RECURRENCE_FREQUENCIES.WEEKLY | RECURRENCE_FREQUENCIES.ALTERNATE_DAY
  selectedDays = [],
  onChangeDays,
  untilDate = '',
  onChangeUntil,
  disabled,
  t
}) => {
  const handleDayClick = (dayId) => {
    if (disabled) return;

    if (mode === RECURRENCE_FREQUENCIES.WEEKLY) {
      if (selectedDays.includes(dayId)) {
        onChangeDays(selectedDays.filter(d => d !== dayId));
      } else {
        onChangeDays([...selectedDays, dayId]);
      }
    } else if (mode === RECURRENCE_FREQUENCIES.ALTERNATE_DAY) {
      if (MWF.includes(dayId)) {
        onChangeDays(MWF);
      } else if (TTS.includes(dayId)) {
        onChangeDays(TTS);
      }
    }
  };

  // Set defaults for empty states
  React.useEffect(() => {
    if (!disabled && selectedDays.length === 0) {
      if (mode === RECURRENCE_FREQUENCIES.ALTERNATE_DAY) {
        onChangeDays(MWF);
      } else if (mode === RECURRENCE_FREQUENCIES.WEEKLY) {
        onChangeDays(['M']);
      }
    }
  }, [mode, selectedDays, disabled, onChangeDays]);

  return (
    <div className="recurrence-advanced">
      <div className="days-selector">
        {WEEK_DAYS.map((day, index) => (
          <div
            key={`${day.id}-${index}`}
            className={`day-circle ${selectedDays.includes(day.id) ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={() => handleDayClick(day.id)}
          >
            {day.label}
          </div>
        ))}
      </div>
      <div className="until-picker">
        <label>{t('dailyStandup.settings.userCard.until')}</label>
        <input
          type="date"
          value={untilDate}
          min={new Date().toISOString().split('T')[0]}
          onChange={(e) => onChangeUntil(e.target.value)}
          disabled={disabled}
          readOnly={disabled}
        />
      </div>
    </div>
  );
};

const SettingsSkeleton = () => (
  <div className="settings-tab-container skeleton-wrapper">
    <Box className="settings-card">
      <div className="card-header">
        <div className="title-container">
          <Skeleton variant="circular" width={40} height={40} />
          <Box className="skeleton-ml-1-5">
            <Skeleton variant="text" width={150} height={24} />
            <Skeleton variant="text" width={250} height={16} />
          </Box>
        </div>
        <Skeleton variant="rectangular" width={48} height={24} className="skeleton-chip" />
      </div>
      <div className="card-content">
        <div className="form-row skeleton-form-row">
          <Box className="skeleton-flex-1">
            <Skeleton variant="text" width={100} height={16} />
            <Skeleton variant="rectangular" height={40} className="skeleton-input" />
          </Box>
          <Box className="skeleton-flex-1">
            <Skeleton variant="text" width={100} height={16} />
            <Skeleton variant="rectangular" height={40} className="skeleton-input" />
          </Box>
          <Box className="skeleton-flex-1-5">
            <Skeleton variant="text" width={100} height={16} />
            <Skeleton variant="rectangular" height={40} className="skeleton-input" />
          </Box>
          <Box className="skeleton-flex-1-5">
            <Skeleton variant="text" width={100} height={16} />
            <Skeleton variant="rectangular" height={40} className="skeleton-input" />
          </Box>
        </div>
      </div>
    </Box>

    <Box className="settings-section-divider" />

    <div className="settings-section-header">
      <Box>
        <Skeleton variant="text" width={180} height={28} />
        <Skeleton variant="text" width={300} height={16} />
      </Box>
      <Box className="skeleton-gap-2-center">
        <Skeleton variant="rectangular" width={80} height={32} className="skeleton-chip" />
        <Skeleton variant="rectangular" width={180} height={32} className="skeleton-chip" />
      </Box>
    </div>

    <div className="project-settings-columns">
      <div className="settings-column">
        <Box className="settings-card email-config-card">
          <div className="card-header">
            <div className="title-container">
              <Skeleton variant="circular" width={40} height={40} />
              <Box className="skeleton-ml-1-5">
                <Skeleton variant="text" width={140} height={24} />
                <Skeleton variant="text" width={220} height={16} />
              </Box>
            </div>
          </div>
          <div className="card-content">
             <Box className="skeleton-mb-2">
               <Skeleton variant="text" width={100} height={16} />
               <Skeleton variant="rectangular" height={40} className="skeleton-input" />
             </Box>
             <Box>
               <Skeleton variant="text" width={100} height={16} />
               <Skeleton variant="rectangular" height={40} className="skeleton-input" />
             </Box>
          </div>
        </Box>
        <Box className="settings-card stagnation-card">
          <div className="card-header">
            <div className="title-container">
              <Skeleton variant="circular" width={40} height={40} />
              <Box className="skeleton-ml-1-5">
                <Skeleton variant="text" width={140} height={24} />
                <Skeleton variant="text" width={220} height={16} />
              </Box>
            </div>
            <Skeleton variant="rectangular" width={48} height={24} className="skeleton-chip" />
          </div>
          <div className="card-content">
            <Box className="skeleton-gap-1-mb-2">
              <Skeleton variant="rectangular" width={100} height={32} className="skeleton-chip" />
              <Skeleton variant="rectangular" width={100} height={32} className="skeleton-chip" />
            </Box>
            <Box className="skeleton-gap-2-center">
              <Skeleton variant="text" width={120} height={16} />
              <Skeleton variant="rectangular" width={150} height={36} className="skeleton-input" />
            </Box>
          </div>
        </Box>
      </div>
      
      <div className="settings-column">
        <Box className="settings-card dsm-reminder-card">
          <div className="card-header">
            <div className="title-container">
              <Skeleton variant="circular" width={40} height={40} />
              <Box className="skeleton-ml-1-5">
                <Skeleton variant="text" width={140} height={24} />
                <Skeleton variant="text" width={220} height={16} />
              </Box>
            </div>
            <Skeleton variant="rectangular" width={48} height={24} className="skeleton-chip" />
          </div>
          <div className="card-content">
             <Box className="skeleton-mb-2">
               <Skeleton variant="text" width={100} height={16} />
               <Skeleton variant="rectangular" height={40} className="skeleton-input" />
             </Box>
             <Box>
               <Skeleton variant="text" width={100} height={16} />
               <Skeleton variant="rectangular" height={40} className="skeleton-input" />
             </Box>
          </div>
        </Box>
        <Box className="settings-card standup-not-updated-card">
          <div className="card-header">
            <div className="title-container">
              <Skeleton variant="circular" width={40} height={40} />
              <Box className="skeleton-ml-1-5">
                <Skeleton variant="text" width={140} height={24} />
                <Skeleton variant="text" width={220} height={16} />
              </Box>
            </div>
            <Skeleton variant="rectangular" width={48} height={24} className="skeleton-chip" />
          </div>
          <div className="card-content">
            <Box className="skeleton-gap-1-mb-2">
              <Skeleton variant="rectangular" width={100} height={32} className="skeleton-chip" />
              <Skeleton variant="rectangular" width={100} height={32} className="skeleton-chip" />
            </Box>
            <Box className="skeleton-gap-2-center">
              <Skeleton variant="text" width={120} height={16} />
              <Skeleton variant="rectangular" width={150} height={36} className="skeleton-input" />
            </Box>
          </div>
        </Box>
      </div>
    </div>
  </div>
);

const SettingsTab = ({ selectedProjectId, projectDropdownsByUser }) => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.auth);
  const { scrumRole, scrumRoleLoading } = useSelector(state => state.scrum);
  const { userEmails } = useSelector(state => state.aiDev);
  const dispatch = useDispatch();

  // Dummy data initialization
  const [userConfig, setUserConfig] = useState(null);
  const [userConfigBeforeEdit, setUserConfigBeforeEdit] = useState(null);
  const [projectConfig, setProjectConfig] = useState(null);
  const [isEditingUserConfig, setIsEditingUserConfig] = useState(false);
  const [isEditingProjectConfig, setIsEditingProjectConfig] = useState(false);
  const [projectConfigBeforeEdit, setProjectConfigBeforeEdit] = useState(null);
  
  const [isSavingUserConfig, setIsSavingUserConfig] = useState(false);
  const [isSavingProjectConfig, setIsSavingProjectConfig] = useState(false);
  
  const [userTimeAnchor, setUserTimeAnchor] = useState(null);
  const [projectTimeAnchor, setProjectTimeAnchor] = useState(null);

  // Local project filter state (for Project Settings)
  const getValidProjectId = React.useCallback((id) => {
    if (id && id !== 'all') return id;
    const firstProject = projectDropdownsByUser?.find(p => p.id !== 'all');
    return firstProject ? firstProject.id : '';
  }, [projectDropdownsByUser]);

  const [activeProjectId, setActiveProjectId] = useState(getValidProjectId(selectedProjectId));
  
  const activeProject = projectDropdownsByUser?.find(p => p.id === activeProjectId);

  useEffect(() => {
    const validId = getValidProjectId(selectedProjectId);
    setActiveProjectId(validId);
  }, [selectedProjectId, getValidProjectId]);

  useEffect(() => {
    if (activeProjectId && activeProjectId !== 'all') {
      dispatch(clearScrumData());
      const project = projectDropdownsByUser?.find(p => p.id === activeProjectId);
      if (project && project.project_type?.toLowerCase() === PROJECT_TYPES.SCRUM.toLowerCase()) {
        dispatch(fetchScrumRole(activeProjectId));
      }
    }
  }, [activeProjectId, projectDropdownsByUser, dispatch]);

  // Permissions
  const userRole = user?.role?.toLowerCase() || '';
  
  const isScrumMaster = (!scrumRoleLoading && scrumRole)
    ? scrumRole.scrum_role === BACKEND_SCRUM_ROLES.SCRUM_MASTER 
    : (activeProject?.scrum_master?.id === user?.id || activeProject?.scrum_master === user?.id);
    
  const isProductOwner = (!scrumRoleLoading && scrumRole)
    ? scrumRole.scrum_role === BACKEND_SCRUM_ROLES.PRODUCT_OWNER 
    : (activeProject?.product_owner?.some?.(po => po.id === user?.id || po === user?.id));
    
  const isProjectAdmin = userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.PM || isScrumMaster || isProductOwner;

  const fetchUserDsmConfig = React.useCallback(async () => {
    try {
      const response = await api.get(ENDPOINTS.USER_PREFERENCES('user_dsm_config'));
      const config = response.data?.data?.user_dsm_config;
      if (config) {
        const mappedConfig = {
          enabled: config.enable ?? config.enabled ?? true,
          frequency: config.frequency === 'ALTERNATE' ? RECURRENCE_FREQUENCIES.ALTERNATE_DAY : (config.frequency || RECURRENCE_FREQUENCIES.DAILY),
          time: config.time || '',
          to: config.to || [],
          cc: config.cc || [],
          selected_days: config.recurrence_day ? config.recurrence_day.map(mapNumToDayId).filter(Boolean) : [],
          until_date: config.until || '',
          time_zone: config.time_zone || getBrowserTimeZone()
        };
        setUserConfig(mappedConfig);
        setUserConfigBeforeEdit(mappedConfig);
      } else {
        const initial = getInitialUserConfig();
        setUserConfig(initial);
        setUserConfigBeforeEdit(initial);
      }
    } catch (error) {
      console.error('Failed to fetch user dsm preference:', error);
      const initial = getInitialUserConfig();
      setUserConfig(initial);
      setUserConfigBeforeEdit(initial);
    }
  }, []);

  const saveUserDsmConfig = async (currentConfig, showToast = true) => {
    setIsSavingUserConfig(true);
    try {
      const configToSave = currentConfig || userConfig;

      const recurrenceDays = (configToSave.selected_days || []).map(mapDayIdToNum).filter(n => n !== null);

      const preference_value = {
        frequency: configToSave.frequency === RECURRENCE_FREQUENCIES.ALTERNATE_DAY ? 'ALTERNATE' : (configToSave.frequency || 'DAILY'),
        time: configToSave.time || '',
        to: configToSave.to || [],
        cc: configToSave.cc || [],
        recurrence_day: recurrenceDays,
        until: configToSave.until_date || null,
        enable: !!configToSave.enabled,
        time_zone: getBrowserTimeZone()
      };

      const body = {
        preference_key: 'user_dsm_config',
        preference_value
      };

      await api.post(ENDPOINTS.SAVE_USER_PREFERENCES, body);

      setUserConfigBeforeEdit({ ...configToSave });
      if (showToast) {
        toast.success(t('dailyStandup.settings.success.savePreferences') || 'Preferences saved successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to save preferences.');
      if (userConfigBeforeEdit) {
        setUserConfig(userConfigBeforeEdit);
      }
    } finally {
      setIsSavingUserConfig(false);
    }
  };

  const fetchProjectConfig = React.useCallback(async (projectId) => {
    const targetProjectId = projectId || activeProjectId;
    if (!targetProjectId) {
      const initial = getInitialProjectConfig();
      setProjectConfig(initial);
      setProjectConfigBeforeEdit(initial);
      return;
    }

    try {
      const response = await api.get(ENDPOINTS.GET_SETTING('PROJECT_REMINDER_CONFIG'), {
        params: { project_id: targetProjectId }
      });
      const setting = response.data?.data;
      if (setting && setting.json_value) {
        const mapped = mapSettingToUiProjectConfig(setting.json_value);
        setProjectConfig(mapped);
        setProjectConfigBeforeEdit(mapped);
      } else {
        const initial = getInitialProjectConfig();
        setProjectConfig(initial);
        setProjectConfigBeforeEdit(initial);
      }
    } catch (error) {
      console.error('Failed to fetch project reminder config:', error);
      const initial = getInitialProjectConfig();
      setProjectConfig(initial);
      setProjectConfigBeforeEdit(initial);
    }
  }, [activeProjectId]);

  const saveProjectConfig = async (configToSave, showToast = true) => {
    if (!activeProjectId) {
      if (showToast) toast.error(t('dailyStandup.settings.errors.selectSpecificProject'));
      return;
    }
    setIsSavingProjectConfig(true);
    try {
      const json_value = mapUiProjectConfigToSetting(configToSave || projectConfig);

      const body = {
        project_id: activeProjectId,
        json_value
      };

      await api.put(ENDPOINTS.UPDATE_SETTING('PROJECT_REMINDER_CONFIG'), body);

      setProjectConfigBeforeEdit(JSON.parse(JSON.stringify(configToSave || projectConfig)));
      if (showToast) {
        toast.success(t('dailyStandup.settings.success.saveProjectConfig') || 'Project configuration saved successfully!');
      }
    } catch (error) {
      console.error('Failed to save project reminder config:', error);
      toast.error(error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to save project configuration.');
      if (projectConfigBeforeEdit) {
        setProjectConfig(JSON.parse(JSON.stringify(projectConfigBeforeEdit)));
      }
    } finally {
      setIsSavingProjectConfig(false);
    }
  };

  useEffect(() => {
    fetchUserDsmConfig();
    if (activeProjectId) {
      fetchProjectConfig(activeProjectId);
      dispatch(fetchAIPlanDropdown({ projectId: activeProjectId, type: 'user_emails' }));
    }
  }, [activeProjectId, dispatch, fetchUserDsmConfig, fetchProjectConfig]);

  const handleUserConfigChange = (field, value) => {
    setUserConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProjectConfigChange = (section, field, value) => {
    if (!isProjectAdmin) return;
    if (!isEditingProjectConfig && field !== 'enabled') return;
    setProjectConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleProjectChannelsChange = (section, channel) => {
    if (!isProjectAdmin || !isEditingProjectConfig) return;
    setProjectConfig(prev => {
      const currentChannels = prev[section].channels;
      const newValue = !currentChannels[channel];
      if (!newValue && Object.keys(currentChannels).filter(k => k !== channel && currentChannels[k]).length === 0) {
        return prev;
      }
      return {
        ...prev,
        [section]: {
          ...prev[section],
          channels: { ...currentChannels, [channel]: newValue }
        }
      };
    });
  };
  const [emailErrors, setEmailErrors] = useState({ userTo: '', userCc: '', reportTo: '', reportCc: '' });

  const validateEmail = (email) => {
    if (!email) return false;
    const cleaned = email.replace(REGEX.emailWhitespace, '').replace(REGEX.emailInvisibleChars, '').trim();
    if (!cleaned) return false;
    if (!REGEX.email.test(cleaned)) return false;
    if (cleaned.startsWith('.') || cleaned.endsWith('.')) return false;
    if (cleaned.includes('..')) return false;
    const [localPart, domainPart] = cleaned.split('@');
    if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
    if (domainPart && domainPart.includes('..')) return false;
    return true;
  };

  const normalizeEmail = (email) => String(email || '')
    .replace(REGEX.emailWhitespace, '')
    .replace(REGEX.emailInvisibleChars, '')
    .trim()
    .toLowerCase();

  const validateEmailSelection = (errorKey, newValue, onValid) => {
    const normalizedValue = newValue.map(normalizeEmail);
    const invalidEmail = normalizedValue.find((email) => !validateEmail(email));

    if (invalidEmail) {
      setEmailErrors(prev => ({ ...prev, [errorKey]: t('dailyStandup.settings.errors.invalidEmail', { email: invalidEmail }) }));
      return;
    }

    setEmailErrors(prev => ({ ...prev, [errorKey]: '' }));
    onValid(normalizedValue);
  };

  const handleUserEmailChange = (field, errorKey, newValue) => {
    validateEmailSelection(errorKey, newValue, (normalizedValue) => {
      handleUserConfigChange(field, normalizedValue);
    });
  };

  const handleProjectEmailChange = (field, errorKey, newValue) => {
    validateEmailSelection(errorKey, newValue, (normalizedValue) => {
      handleProjectConfigChange('report_email_configuration', field, normalizedValue);
    });
  };

  const handleSaveUserConfig = async () => {
    if (userConfig.enabled) {
      if (!userConfig.time) {
        toast.error(t('dailyStandup.settings.errors.timeRequired') || 'Send time is required.');
        return;
      }
      if (!userConfig.to || userConfig.to.length === 0) {
        toast.error(t('dailyStandup.settings.errors.toRequired') || 'At least one recipient in "To" is required.');
        return;
      }
      if ((userConfig.frequency === RECURRENCE_FREQUENCIES.WEEKLY || userConfig.frequency === RECURRENCE_FREQUENCIES.ALTERNATE_DAY) && (!userConfig.selected_days || userConfig.selected_days.length === 0)) {
        return;
      }
      if ((userConfig.frequency === RECURRENCE_FREQUENCIES.WEEKLY || userConfig.frequency === RECURRENCE_FREQUENCIES.ALTERNATE_DAY) && !userConfig.until_date) {
        return;
      }
    }
    await saveUserDsmConfig();
    setIsEditingUserConfig(false);
  };

  const handleStartProjectEdit = () => {
    setProjectConfigBeforeEdit(JSON.parse(JSON.stringify(projectConfig)));
    setIsEditingProjectConfig(true);
    setIsEditingUserConfig(false);
  };

  const handleCancelProjectEdit = () => {
    if (projectConfigBeforeEdit) setProjectConfig(projectConfigBeforeEdit);
    setIsEditingProjectConfig(false);
    setProjectConfigBeforeEdit(null);
    setEmailErrors(prev => ({ ...prev, reportTo: '', reportCc: '' }));
  };

  const handleSaveProjectEdit = async () => {
    if (projectConfig.dsm_updates_on_mail.enabled) {
      if (!projectConfig.dsm_updates_on_mail.send_time) {
        toast.error(t('dailyStandup.settings.errors.timeRequired') || 'Send time is required.');
        return;
      }
      if (!projectConfig.report_email_configuration.to || projectConfig.report_email_configuration.to.length === 0) {
        toast.error(t('dailyStandup.settings.errors.toRequired') || 'At least one recipient in "To" is required.');
        return;
      }
    }
    await saveProjectConfig();
    setIsEditingProjectConfig(false);
  };

  const isUserConfigChanged = userConfig && userConfigBeforeEdit && JSON.stringify(userConfig) !== JSON.stringify(userConfigBeforeEdit);
  const isProjectConfigChanged = projectConfig && projectConfigBeforeEdit && JSON.stringify(projectConfig) !== JSON.stringify(projectConfigBeforeEdit);
  const isUserConfigComplete = !userConfig?.enabled || (
    Boolean(userConfig.time) &&
    userConfig.to?.length > 0 &&
    (userConfig.frequency === RECURRENCE_FREQUENCIES.DAILY || (
      userConfig.selected_days?.length > 0 && Boolean(userConfig.until_date)
    ))
  );

  if (!userConfig || !projectConfig) return <SettingsSkeleton />;

  return (
    <div className="settings-tab-container">

      {/* User Specific Config */}
      <Box className={`settings-card user-config-card ${!userConfig.enabled ? 'is-toggled-off' : ''}`}>
        <div className="card-header">
          <div className="title-container">
            <div className="icon-container">
              <EmailOutlined />
            </div>
            <div>
              <h3>
                {t('dailyStandup.settings.userCard.title')}
              </h3>
              <p>{t('dailyStandup.settings.userCard.subtitle')}</p>
            </div>
          </div>
          <Box className="card-header-actions card-header-actions--raised">
            {!isEditingUserConfig && !isEditingProjectConfig && (
              <Button variant="outlined" className="user-edit-btn" onClick={() => {
                setIsEditingUserConfig(true);
                setIsEditingProjectConfig(false);
              }} startIcon={<EditOutlined fontSize="small" />}>
                {t('common.edit')}
              </Button>
            )}
            {isEditingUserConfig && (
              <Switch
                className="settings-switch"
                checked={userConfig.enabled}
                onChange={(e) => handleUserConfigChange('enabled', e.target.checked)}
                disabled={isSavingUserConfig}
              />
            )}
          </Box>
        </div>

        <div className="card-content">
              <div className={`form-row${isEditingUserConfig && (userConfig.frequency === RECURRENCE_FREQUENCIES.WEEKLY || userConfig.frequency === RECURRENCE_FREQUENCIES.ALTERNATE_DAY) ? ' has-day-strip' : ''}`}>
                <div className="form-group form-group--recurrence">
                  <label>{t('dailyStandup.settings.userCard.recurrenceLabel')}</label>
                  <Select
                    value={userConfig.frequency}
                    onChange={(e) => {
                      const newFreq = e.target.value;
                      handleUserConfigChange('frequency', newFreq);
                      if (newFreq === RECURRENCE_FREQUENCIES.WEEKLY) {
                        handleUserConfigChange('selected_days', ['M']);
                      } else if (newFreq === RECURRENCE_FREQUENCIES.ALTERNATE_DAY) {
                        handleUserConfigChange('selected_days', ['M', 'W', 'F']);
                      }
                    }}
                    className={`settings-input ${isEditingUserConfig ? 'is-editing' : 'is-readonly'}`}
                    size="small"
                    inputProps={{ readOnly: !isEditingUserConfig }}
                    renderValue={(selected) => {
                      const displayValue = selected === RECURRENCE_FREQUENCIES.ALTERNATE_DAY ? t('dailyStandup.settings.userCard.recurrenceAlternateDay') :
                        selected === RECURRENCE_FREQUENCIES.WEEKLY ? t('dailyStandup.settings.userCard.recurrenceWeekly') : t('dailyStandup.settings.userCard.recurrenceDaily');
                      return (
                        <Box display="flex" alignItems="center" gap={1}>
                          <EmailOutlined className="input-icon" /> {displayValue}
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value={RECURRENCE_FREQUENCIES.DAILY}>{t('dailyStandup.settings.userCard.recurrenceDaily')}</MenuItem>
                    <MenuItem value={RECURRENCE_FREQUENCIES.ALTERNATE_DAY}>{t('dailyStandup.settings.userCard.recurrenceAlternateDay')}</MenuItem>
                    <MenuItem value={RECURRENCE_FREQUENCIES.WEEKLY}>{t('dailyStandup.settings.userCard.recurrenceWeekly')}</MenuItem>
                  </Select>
                  {isEditingUserConfig && (userConfig.frequency === RECURRENCE_FREQUENCIES.WEEKLY || userConfig.frequency === RECURRENCE_FREQUENCIES.ALTERNATE_DAY) && (
                    <div className="recurrence-advanced-wrapper">
                      <DaySelector
                        mode={userConfig.frequency}
                        selectedDays={userConfig.selected_days || []}
                        onChangeDays={(days) => handleUserConfigChange('selected_days', days)}
                        untilDate={userConfig.until_date || ''}
                        onChangeUntil={(date) => handleUserConfigChange('until_date', date)}
                        disabled={!isEditingUserConfig}
                        t={t}
                      />
                    </div>
                  )}
                </div>
                <div className="form-group form-group--time">
                  <label>{t('dailyStandup.settings.userCard.sendTimeLabel')}</label>
                  <TextField
                    value={userConfig.time ? format12h(userConfig.time) : ''}
                    onClick={(e) => { if (isEditingUserConfig) setUserTimeAnchor(e.currentTarget); }}
                    className={`settings-input ${isEditingUserConfig ? 'is-editing' : 'is-readonly'}`}
                    size="small"
                    placeholder={t('dailyStandup.settings.userCard.sendTimePlaceholder')}
                    InputProps={{
                      readOnly: true,
                      style: { cursor: isEditingUserConfig ? 'pointer' : 'default' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTimeFilled className="input-icon" />
                        </InputAdornment>
                      )
                    }}
                  />
                  <TimePickerModal
                    showClear={true}
                    open={Boolean(userTimeAnchor)}
                    anchorEl={userTimeAnchor}
                    onClose={() => setUserTimeAnchor(null)}
                    value={userConfig.time}
                    onChange={(val) => handleUserConfigChange('time', val)}
                  />
                </div>
                <div className="form-group form-group--email">
                  <label>{t('dailyStandup.settings.userCard.toLabel')}</label>
                  <Autocomplete
                    multiple
                    freeSolo
                    disableClearable
                    readOnly={!isEditingUserConfig}
                    options={userEmails?.filter(u => u?.email)?.map(u => u.email) || []}
                    value={userConfig.to}
                    onChange={(e, v) => handleUserEmailChange('to', 'userTo', v)}
                    onInputChange={() => setEmailErrors(prev => ({ ...prev, userTo: '' }))}
                    className={`settings-input autocomplete-input ${isEditingUserConfig ? 'is-editing' : 'is-readonly'} ${userConfig.to.length > 0 ? 'has-value' : ''}`}
                    componentsProps={{
                      popper: {
                        modifiers: [
                          { name: 'offset', options: { offset: [0, 8] } },
                        ],
                      },
                    }}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <li key={key} {...otherProps}>
                          <OverflowTooltip title={option} tooltipWidth={400} placement="top-start">
                            {option}
                          </OverflowTooltip>
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) => {
                      if (isEditingUserConfig || value.length <= 1) {
                        return value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} size="small" />
                        ));
                      }
                      const firstEmail = value[0];
                      const extraCount = value.length - 1;
                      return [
                        <Chip key="first" variant="outlined" label={firstEmail} size="small" />,
                        <Tooltip key="extra" title={value.join(', ')} placement="top" classes={{ tooltip: 'app-tooltip' }}>
                          <Chip variant="outlined" label={`+${extraCount}`} size="small" className="additional-emails-chip" />
                        </Tooltip>
                      ];
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('dailyStandup.settings.userCard.addEmailPlaceholder')}
                        size="small"
                        error={!!emailErrors.userTo}
                        helperText={emailErrors.userTo || undefined}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'off',
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {params.InputProps.endAdornment}
                              {!isEditingUserConfig && (
                                <span className="read-only-input-adornment">
                                  <UserIcon />
                                </span>
                              )}
                            </React.Fragment>
                          )
                        }}
                      />
                    )}
                  />
                </div>
                <div className="form-group form-group--email">
                  <label>{t('dailyStandup.settings.userCard.ccLabel')}</label>
                  <Autocomplete
                    multiple
                    freeSolo
                    disableClearable
                    readOnly={!isEditingUserConfig}
                    options={userEmails?.filter(u => u?.email)?.map(u => u.email) || []}
                    value={userConfig.cc}
                    onChange={(e, v) => handleUserEmailChange('cc', 'userCc', v)}
                    onInputChange={() => setEmailErrors(prev => ({ ...prev, userCc: '' }))}
                    className={`settings-input autocomplete-input ${isEditingUserConfig ? 'is-editing' : 'is-readonly'} ${userConfig.cc.length > 0 ? 'has-value' : ''}`}
                    componentsProps={{
                      popper: {
                        modifiers: [
                          { name: 'offset', options: { offset: [0, 8] } },
                        ],
                      },
                    }}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <li key={key} {...otherProps}>
                          <OverflowTooltip title={option} tooltipWidth={400} placement="top-start">
                            {option}
                          </OverflowTooltip>
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) => {
                      if (isEditingUserConfig || value.length <= 1) {
                        return value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} size="small" />
                        ));
                      }
                      const firstEmail = value[0];
                      const extraCount = value.length - 1;
                      return [
                        <Chip key="first" variant="outlined" label={firstEmail} size="small" />,
                        <Tooltip key="extra" title={value.join(', ')} placement="top" classes={{ tooltip: 'app-tooltip' }}>
                          <Chip variant="outlined" label={`+${extraCount}`} size="small" className="additional-emails-chip" />
                        </Tooltip>
                      ];
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('dailyStandup.settings.userCard.addEmailPlaceholder')}
                        size="small"
                        error={!!emailErrors.userCc}
                        helperText={emailErrors.userCc || undefined}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'off',
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {params.InputProps.endAdornment}
                              {!isEditingUserConfig && (
                                <span className="read-only-input-adornment">
                                  <UserIcon />
                                </span>
                              )}
                            </React.Fragment>
                          )
                        }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            {isEditingUserConfig && (
              <div className="card-actions">
                <Button variant="outlined" className="cancel-btn" onClick={() => {
                  if (userConfigBeforeEdit) {
                    setUserConfig({ ...userConfigBeforeEdit });
                  }
                  setIsEditingUserConfig(false);
                  setEmailErrors(prev => ({ ...prev, userTo: '', userCc: '' }));
                }}>
                  {t('dailyStandup.settings.userCard.cancel')}
                </Button>
                <Button variant="contained" className="save-btn" onClick={() => {
                  handleSaveUserConfig();
                }} disabled={!isUserConfigChanged || !isUserConfigComplete || isSavingUserConfig}>
                  {t('dailyStandup.settings.userCard.saveChanges')}
                </Button>
              </div>
            )}
      </Box>

      <Box className="settings-section-divider" />

      {/* Project Specific Config */}
      <div className="settings-section-header">
        <div>
          <h2 className="section-title">{t('dailyStandup.settings.projectSection.title')}</h2>
          <p className="section-subtitle">{t('dailyStandup.settings.projectSection.subtitle')}</p>
        </div>
        <div className="project-settings-controls">
          {isProjectAdmin && !isEditingUserConfig && (isEditingProjectConfig ? (
            <div className="project-settings-actions">
              <Button variant="outlined" className="cancel-btn" onClick={handleCancelProjectEdit}>
                {t('dailyStandup.settings.userCard.cancel')}
              </Button>
              <Button variant="contained" className="save-btn" onClick={handleSaveProjectEdit} disabled={!isProjectConfigChanged || isSavingProjectConfig}>
                {t('dailyStandup.settings.userCard.saveChanges')}
              </Button>
            </div>
          ) : (
            <Button variant="outlined" className="project-edit-btn" onClick={handleStartProjectEdit} startIcon={<EditOutlined fontSize="small" />}>
              {t('common.edit')}
            </Button>
          ))}
          <div className="project-selector">
            <span>{t('dailyStandup.settings.projectSection.projectLabel')}</span>
            <Select
              value={activeProjectId}
              onChange={(e) => setActiveProjectId(e.target.value)}
              disabled={isEditingProjectConfig}
              size="small"
              disableUnderline
              MenuProps={{ 
                PaperProps: { className: 'daily-standup-dropdown-menu settings-project-dropdown-menu' },
                anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                transformOrigin: { vertical: 'top', horizontal: 'right' }
              }}
              renderValue={(selected) => {
                const selectedProject = projectDropdownsByUser?.find(p => p.id === selected);
                const projectName = selectedProject ? selectedProject.name : 'Orcaflo';
                return (
                  <OverflowTooltip title={projectName} placement="bottom" tooltipWidth={350} className="project-name-tooltip">
                    {projectName}
                  </OverflowTooltip>
                );
              }}
            >
              {projectDropdownsByUser?.filter(p => p.id !== 'all').map(p => (
                <MenuItem key={p.id} value={p.id} className="daily-standup-menu-item">
                  <OverflowTooltip title={p.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start">
                    {p.name}
                  </OverflowTooltip>
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <div className="project-settings-columns">

        <div className="settings-column">
          {/* Email Configuration */}
          <Box className={`settings-card email-config-card ${!isProjectAdmin ? 'disabled' : ''}`}>
            <div className="card-header">
              <div className="title-container">
                <div className="icon-container">
                  <EmailOutlined />
                </div>
                <div>
                  <h3>
                    {t('dailyStandup.settings.emailConfig.title')}
                  </h3>
                  <p>{t('dailyStandup.settings.emailConfig.subtitle')}</p>
                </div>
              </div>
            </div>
            <div className="card-content">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('dailyStandup.settings.emailConfig.toLabel')}</label>
                  <Autocomplete
                    multiple
                    freeSolo
                    disableClearable
                    readOnly={!isEditingProjectConfig}
                    options={userEmails?.filter(u => u?.email)?.map(u => u.email) || []}
                    value={projectConfig.report_email_configuration.to}
                    onChange={(e, v) => handleProjectEmailChange('to', 'reportTo', v)}
                    onInputChange={() => setEmailErrors(prev => ({ ...prev, reportTo: '' }))}
                    className={`settings-input autocomplete-input ${isEditingProjectConfig ? 'is-editing' : 'is-readonly'} ${projectConfig.report_email_configuration.to.length > 0 ? 'has-value' : ''}`}
                    componentsProps={{
                      popper: {
                        modifiers: [
                          { name: 'offset', options: { offset: [0, 8] } },
                        ],
                      },
                    }}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <li key={key} {...otherProps}>
                          <OverflowTooltip title={option} tooltipWidth={400} placement="top-start">
                            {option}
                          </OverflowTooltip>
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) => {
                      if (isEditingProjectConfig || value.length <= 1) {
                        return value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} size="small" />
                        ));
                      }
                      const firstEmail = value[0];
                      const extraCount = value.length - 1;
                      return [
                        <Chip key="first" variant="outlined" label={firstEmail} size="small" />,
                        <Tooltip key="extra" title={value.join(', ')} placement="top" classes={{ tooltip: 'app-tooltip' }}>
                          <Chip variant="outlined" label={`+${extraCount}`} size="small" className="additional-emails-chip" />
                        </Tooltip>
                      ];
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('dailyStandup.settings.userCard.addEmailPlaceholder')}
                        size="small"
                        error={!!emailErrors.reportTo}
                        helperText={emailErrors.reportTo || undefined}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'off',
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {params.InputProps.endAdornment}
                              {!isEditingProjectConfig && (
                                <span className="read-only-input-adornment">
                                  <UserIcon />
                                </span>
                              )}
                            </React.Fragment>
                          )
                        }}
                      />
                    )}
                  />
                </div>
                <div className="form-group">
                  <label>{t('dailyStandup.settings.emailConfig.ccLabel')}</label>
                  <Autocomplete
                    multiple
                    freeSolo
                    disableClearable
                    readOnly={!isEditingProjectConfig}
                    options={userEmails?.filter(u => u?.email)?.map(u => u.email) || []}
                    value={projectConfig.report_email_configuration.cc}
                    onChange={(e, v) => handleProjectEmailChange('cc', 'reportCc', v)}
                    onInputChange={() => setEmailErrors(prev => ({ ...prev, reportCc: '' }))}
                    className={`settings-input autocomplete-input ${isEditingProjectConfig ? 'is-editing' : 'is-readonly'} ${projectConfig.report_email_configuration.cc.length > 0 ? 'has-value' : ''}`}
                    componentsProps={{
                      popper: {
                        modifiers: [
                          { name: 'offset', options: { offset: [0, 8] } },
                        ],
                      },
                    }}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <li key={key} {...otherProps}>
                          <OverflowTooltip title={option} tooltipWidth={400} placement="top-start">
                            {option}
                          </OverflowTooltip>
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) => {
                      if (isEditingProjectConfig || value.length <= 1) {
                        return value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} size="small" />
                        ));
                      }
                      const firstEmail = value[0];
                      const extraCount = value.length - 1;
                      return [
                        <Chip key="first" variant="outlined" label={firstEmail} size="small" />,
                        <Tooltip key="extra" title={value.join(', ')} placement="top" classes={{ tooltip: 'app-tooltip' }}>
                          <Chip variant="outlined" label={`+${extraCount}`} size="small" className="additional-emails-chip" />
                        </Tooltip>
                      ];
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('dailyStandup.settings.userCard.addEmailPlaceholder')}
                        size="small"
                        error={!!emailErrors.reportCc}
                        helperText={emailErrors.reportCc || undefined}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'off',
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {params.InputProps.endAdornment}
                              {!isEditingProjectConfig && (
                                <span className="read-only-input-adornment">
                                  <UserIcon />
                                </span>
                              )}
                            </React.Fragment>
                          )
                        }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </Box>

          {/* Task Stagnation Reminder */}
          <Box className={`settings-card stagnation-card ${!isProjectAdmin ? 'disabled' : ''} ${!projectConfig.task_stagnation_reminder.enabled ? 'is-toggled-off' : ''}`}>
            <div className="card-header">
              <div className="title-container">
                <div className="icon-container">
                  <AccessTime />
                </div>
                <div>
                  <h3>
                    {t('dailyStandup.settings.stagnation.title')}
                  </h3>
                  <p>{t('dailyStandup.settings.stagnation.subtitle')}</p>
                </div>
              </div>
              <Box className="card-header-actions">
                {isEditingProjectConfig && (
                  <Switch
                    className="settings-switch settings-switch--raised"
                    checked={projectConfig.task_stagnation_reminder.enabled}
                    onChange={(e) => handleProjectConfigChange('task_stagnation_reminder', 'enabled', e.target.checked)}
                    disabled={!isProjectAdmin || isSavingProjectConfig}
                  />
                )}
                {!isProjectAdmin && (
                  <span className="badge-warning">{t('dailyStandup.settings.standupNotUpdated.pmSmOnly')}</span>
                )}
              </Box>
            </div>
                <div className="card-content">
                <div className="channels-group">
                  <div className="reminder-time-box stagnation">
                    <label>{t('dailyStandup.settings.stagnation.reminderTimeLabel')}</label>
                    <span className="reminder-time-separator" />
                    {isEditingProjectConfig ? (
                      <Select
                        value={projectConfig.task_stagnation_reminder.reminder_time || DEFAULT_REMINDER_TIME}
                        onChange={(e) => handleProjectConfigChange('task_stagnation_reminder', 'reminder_time', e.target.value)}
                        className="reminder-time-select"
                        size="small"
                        IconComponent={KeyboardArrowDown}
                        disableUnderline
                        variant="standard"
                        MenuProps={{ PaperProps: { className: 'daily-standup-dropdown-menu' } }}
                        renderValue={(selected) => {
                          const opt = REMINDER_TIME_OPTIONS.find(o => o.value === selected);
                          return opt ? opt.label : selected;
                        }}
                      >
                        {REMINDER_TIME_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <div className="reminder-time-value">
                        {REMINDER_TIME_OPTIONS.find(opt => opt.value === (projectConfig.task_stagnation_reminder.reminder_time || DEFAULT_REMINDER_TIME))?.label || projectConfig.task_stagnation_reminder.reminder_time}
                      </div>
                    )}
                  </div>
                  <div
                    className={`channel-btn channel-btn--blue ${projectConfig.task_stagnation_reminder.channels.email ? 'active' : ''} ${!isEditingProjectConfig ? 'channel-btn--disabled' : ''}`}
                    onClick={() => handleProjectChannelsChange('task_stagnation_reminder', 'email')}
                  >
                    <EmailOutlined /> {t('dailyStandup.settings.stagnation.emailChannel')}
                  </div>
                  <div
                    className={`channel-btn channel-btn--blue ${projectConfig.task_stagnation_reminder.channels.in_app ? 'active' : ''} ${!isEditingProjectConfig ? 'channel-btn--disabled' : ''}`}
                    onClick={() => handleProjectChannelsChange('task_stagnation_reminder', 'in_app')}
                  >
                    <InAppIcon /> {t('dailyStandup.settings.stagnation.inAppChannel')}
                  </div>
                </div>
              </div>
            </Box>
        </div>

        <div className="settings-column">
          {/* Daily Standup Reminder */}
        <Box className={`settings-card dsm-reminder-card ${!isProjectAdmin ? 'disabled' : ''} ${!projectConfig.dsm_updates_on_mail.enabled ? 'is-toggled-off' : ''}`}>
          <div className="card-header">
            <div className="title-container">
              <div className="icon-container">
                <CalendarMonthOutlined />
              </div>
              <div>
                <h3>
                  {t('dailyStandup.settings.dsmReminder.title')}
                </h3>
                <p>{t('dailyStandup.settings.dsmReminder.subtitle')}</p>
              </div>
            </div>
            <Box className="card-header-actions card-header-actions--dsm">
              {isEditingProjectConfig && (
                <Switch 
                  className="settings-switch"
                  checked={projectConfig.dsm_updates_on_mail.enabled}
                  onChange={(e) => handleProjectConfigChange('dsm_updates_on_mail', 'enabled', e.target.checked)}
                  disabled={!isProjectAdmin || isSavingProjectConfig}
                />
              )}
            </Box>
          </div>
            <div className="card-content">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('dailyStandup.settings.userCard.recurrenceLabel')}</label>
                  <Select
                    value={projectConfig.dsm_updates_on_mail.recurrence}
                    onChange={(e) => {
                      const newFreq = e.target.value;
                      handleProjectConfigChange('dsm_updates_on_mail', 'recurrence', newFreq);
                      if (newFreq === RECURRENCE_FREQUENCIES.WEEKLY) {
                        handleProjectConfigChange('dsm_updates_on_mail', 'selected_days', ['M']);
                      } else if (newFreq === RECURRENCE_FREQUENCIES.ALTERNATE_DAY) {
                        handleProjectConfigChange('dsm_updates_on_mail', 'selected_days', ['M', 'W', 'F']);
                      }
                    }}
                    className={`settings-input ${isEditingProjectConfig ? 'is-editing' : 'is-readonly'}`}
                    size="small"
                    inputProps={{ readOnly: !isEditingProjectConfig }}
                    renderValue={(selected) => {
                      const displayValue = selected === RECURRENCE_FREQUENCIES.ALTERNATE_DAY ? t('dailyStandup.settings.userCard.recurrenceAlternateDay') :
                                           selected === RECURRENCE_FREQUENCIES.WEEKLY ? t('dailyStandup.settings.userCard.recurrenceWeekly') : t('dailyStandup.settings.userCard.recurrenceDaily');
                      return (
                        <Box display="flex" alignItems="center" gap={1}>
                          <EmailOutlined className="input-icon" /> {displayValue}
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value={RECURRENCE_FREQUENCIES.DAILY}>{t('dailyStandup.settings.userCard.recurrenceDaily')}</MenuItem>
                    <MenuItem value={RECURRENCE_FREQUENCIES.ALTERNATE_DAY}>{t('dailyStandup.settings.userCard.recurrenceAlternateDay')}</MenuItem>
                    <MenuItem value={RECURRENCE_FREQUENCIES.WEEKLY}>{t('dailyStandup.settings.userCard.recurrenceWeekly')}</MenuItem>
                  </Select>
                  {isEditingProjectConfig && (projectConfig.dsm_updates_on_mail.recurrence === RECURRENCE_FREQUENCIES.WEEKLY || projectConfig.dsm_updates_on_mail.recurrence === RECURRENCE_FREQUENCIES.ALTERNATE_DAY) && (
                    <div className="recurrence-advanced-wrapper">
                      <DaySelector 
                        mode={projectConfig.dsm_updates_on_mail.recurrence}
                        selectedDays={projectConfig.dsm_updates_on_mail.selected_days || []}
                        onChangeDays={(days) => handleProjectConfigChange('dsm_updates_on_mail', 'selected_days', days)}
                        untilDate={projectConfig.dsm_updates_on_mail.until_date || ''}
                        onChangeUntil={(date) => handleProjectConfigChange('dsm_updates_on_mail', 'until_date', date)}
                        disabled={!isEditingProjectConfig}
                        t={t}
                      />
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>{t('dailyStandup.settings.userCard.sendTimeLabel')}</label>
                  <TextField
                    value={projectConfig.dsm_updates_on_mail.send_time ? format12h(projectConfig.dsm_updates_on_mail.send_time) : ''}
                    onClick={(e) => { if (isEditingProjectConfig) setProjectTimeAnchor(e.currentTarget); }}
                    className={`settings-input ${isEditingProjectConfig ? 'is-editing' : 'is-readonly'}`}
                    size="small"
                    placeholder={t('dailyStandup.settings.userCard.sendTimePlaceholder')}
                    InputProps={{
                      readOnly: true,
                      style: { cursor: isEditingProjectConfig ? 'pointer' : 'default' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTimeFilled className="input-icon" />
                        </InputAdornment>
                      )
                    }}
                  />
                  <TimePickerModal
                    showClear={true}
                    open={Boolean(projectTimeAnchor)}
                    anchorEl={projectTimeAnchor}
                    onClose={() => setProjectTimeAnchor(null)}
                    value={projectConfig.dsm_updates_on_mail.send_time}
                    onChange={(val) => handleProjectConfigChange('dsm_updates_on_mail', 'send_time', val)}
                  />
                </div>
              </div>
            </div>
        </Box>
          
          {/* Standup Not Updated Reminder */}
          <Box className={`settings-card warning-card standup-not-updated-card ${!isProjectAdmin ? 'disabled' : ''} ${!projectConfig.standup_not_updated_reminder.enabled ? 'is-toggled-off' : ''}`}>
            <div className="card-header">
              <div className="title-container">
                <div className="icon-container icon-container--warning">
                  <WarningBellIcon />
                </div>
                <div>
                  <h3>
                    {t('dailyStandup.settings.standupNotUpdated.title')}
                  </h3>
                  <p>{t('dailyStandup.settings.standupNotUpdated.subtitle')}</p>
                </div>
              </div>
              <Box className="card-header-actions card-header-actions--warning">
                {isEditingProjectConfig && (
                  <Switch
                    className="settings-switch"
                    checked={projectConfig.standup_not_updated_reminder.enabled}
                    onChange={(e) => handleProjectConfigChange('standup_not_updated_reminder', 'enabled', e.target.checked)}
                    disabled={!isProjectAdmin || isSavingProjectConfig}
                  />
                )}
                {!isProjectAdmin && (
                  <span className="badge-warning">{t('dailyStandup.settings.standupNotUpdated.pmSmOnly')}</span>
                )}
              </Box>
            </div>
                <div className="card-content">
                <div className="channels-group">
                  <div className="reminder-time-box dsm-update">
                    <label>{t('dailyStandup.settings.standupNotUpdated.reminderTimeLabel')}</label>
                    <span className="reminder-time-separator" />
                    {isEditingProjectConfig ? (
                      <Select
                        value={projectConfig.standup_not_updated_reminder.reminder_time || DEFAULT_REMINDER_TIME}
                        onChange={(e) => handleProjectConfigChange('standup_not_updated_reminder', 'reminder_time', e.target.value)}
                        className="reminder-time-select"
                        size="small"
                        IconComponent={KeyboardArrowDown}
                        disableUnderline
                        variant="standard"
                        MenuProps={{ PaperProps: { className: 'daily-standup-dropdown-menu' } }}
                        renderValue={(selected) => {
                          const opt = REMINDER_TIME_OPTIONS.find(o => o.value === selected);
                          return opt ? opt.label : selected;
                        }}
                      >
                        {REMINDER_TIME_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <div className="reminder-time-value">
                        {REMINDER_TIME_OPTIONS.find(opt => opt.value === (projectConfig.standup_not_updated_reminder.reminder_time || DEFAULT_REMINDER_TIME))?.label || projectConfig.standup_not_updated_reminder.reminder_time}
                      </div>
                    )}
                  </div>
                  <div
                    className={`channel-btn channel-btn--warning ${projectConfig.standup_not_updated_reminder.channels.email ? 'active' : ''} ${!isEditingProjectConfig ? 'channel-btn--disabled' : ''}`}
                    onClick={() => handleProjectChannelsChange('standup_not_updated_reminder', 'email')}
                  >
                    <EmailOutlined /> {t('dailyStandup.settings.standupNotUpdated.emailChannel')}
                  </div>
                  <div
                    className={`channel-btn channel-btn--warning ${projectConfig.standup_not_updated_reminder.channels.in_app ? 'active' : ''} ${!isEditingProjectConfig ? 'channel-btn--disabled' : ''}`}
                    onClick={() => handleProjectChannelsChange('standup_not_updated_reminder', 'in_app')}
                  >
                    <InAppIcon /> {t('dailyStandup.settings.standupNotUpdated.inAppChannel')}
                  </div>
                </div>
              </div>
            </Box>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
